import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../services/restApiService';

/**
 * G2b (plan §9 D-3): the admin-console order & cancellation card.
 *
 * Server-derived view model (`GET /orders/self-serve/mine`) — no client-side
 * window math. The Cancel button renders ONLY for the purchaser inside the
 * signed 7-day window; other admins see the section as informative. Orgs
 * without a self-serve order (404) render nothing.
 */

interface SelfServeMineView {
  order: {
    id: string;
    status: string;
    payment_status: string | null;
    sku: string | null;
    billing_period: string | null;
    total_price: string | null;
    currency: string | null;
    created_at: string;
    cancelled_at: string | null;
    refunded_at: string | null;
    credit_note_state: string;
    activation_completed_at?: string | null;
  };
  current_access_expires_at?: string | null;
  is_purchaser: boolean;
  can_cancel: boolean;
  window_ends_at: string | null;
  support_required: boolean;
  cancellation_state: 'none' | 'requested' | 'cancelled';
}

const SUPPORT_COPY =
  'To discuss cancelling this order, contact Pedagogue support.';

export default function OrderCancellationCard() {
  const [view, setView] = useState<SelfServeMineView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Post-cancel copy renders INDEPENDENTLY of the view: a purchaser's own
  // cancellation revokes their session, so the re-fetch fails by design
  // (staging-caught — the card must not blank out under the customer).
  const [completedCopy, setCompletedCopy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiGet<SelfServeMineView>('/orders/self-serve/mine');
      setView(data);
    } catch {
      // 404 (no self-serve order) or transient failure: render nothing —
      // this card is additive to the organisation page.
      setView(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirmedCancel = async () => {
    setConfirming(false);
    setBusy(true);
    setError(null);
    try {
      const result = await apiPost<{ status: 'cancelled' | 'queued' }>(
        '/orders/self-serve/mine/cancel',
        {},
      );
      const wasPaid = view?.order.payment_status === 'PAID';
      setCompletedCopy(
        result.status === 'cancelled'
          ? wasPaid
            ? 'Your order has been cancelled. Your invoice is void, access has been removed, and your payment will be refunded by bank transfer — we will be in touch to arrange it.'
            : 'Your order has been cancelled. Your invoice is void and access has been removed.'
          : wasPaid
            ? 'Your cancellation has been received and is being processed — your payment will be refunded by bank transfer once it completes.'
            : 'Your cancellation has been received and is being processed.',
      );
      // No re-fetch: completing the cancellation revokes THIS session.
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Could not cancel the order — try again or contact support.';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  if (completedCopy) {
    return (
      <div
        data-testid="order-cancellation-card"
        className="mt-8 rounded-lg border border-gray-200 bg-white p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Your order</h3>
        <p className="text-sm text-gray-700" data-testid="cancel-in-progress">
          {completedCopy}
        </p>
      </div>
    );
  }

  if (!loaded || !view) return null;

  const { order } = view;
  const activationDate = order.activation_completed_at && Number.isFinite(new Date(order.activation_completed_at).getTime())
    ? order.activation_completed_at : null;
  const accessExpiry = activationDate && view.cancellation_state === 'none' && view.current_access_expires_at
    && Number.isFinite(new Date(view.current_access_expires_at).getTime())
    && new Date(view.current_access_expires_at) > new Date(activationDate)
    ? view.current_access_expires_at : null;
  const dateLabel = (value: string) => new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London',
  });
  const amount =
    order.total_price != null
      ? `£${Number(order.total_price).toLocaleString('en-GB', {
          minimumFractionDigits: 2,
        })}`
      : null;

  return (
    <div
      data-testid="order-cancellation-card"
      className="mt-8 rounded-lg border border-gray-200 bg-white p-6"
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Your order</h3>
      <div className="text-sm text-gray-600 mb-4">
        {order.sku === 'WHOLE_SCHOOL' ? 'Whole-School Package' : order.sku}
        {order.billing_period ? ` — billed ${order.billing_period.toLowerCase()}` : ''}
        {amount ? ` — ${amount}` : ''}
      </div>

      {activationDate && <p className="text-sm text-gray-600 mb-3">Access granted on {dateLabel(activationDate)}.</p>}
      {accessExpiry && <p className="text-sm text-gray-600 mb-3">Current school access expires on {dateLabel(accessExpiry)}.</p>}

      {view.cancellation_state === 'cancelled' && (
        <p className="text-sm text-gray-700">
          This order has been cancelled
          {order.payment_status === 'REFUND_DUE'
            ? ' — your refund is being arranged.'
            : order.payment_status === 'REFUNDED'
              ? ' and your payment has been refunded.'
              : '.'}
        </p>
      )}

      {view.cancellation_state === 'requested' && (
        <p className="text-sm text-gray-700" data-testid="cancel-in-progress">
          Cancellation in progress — access will be removed shortly.
        </p>
      )}

      {view.cancellation_state === 'none' && (
        <>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          {view.can_cancel ? (
            <>
              <p className="text-sm text-gray-600 mb-3">
                You can cancel this purchase until{' '}
                {view.window_ends_at
                  ? new Date(view.window_ends_at).toLocaleDateString('en-GB')
                  : 'the end of your 7-day window'}
                . Cancelling voids your invoice and removes access for your
                school.
              </p>
              {!confirming ? (
                <button
                  type="button"
                  className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                  onClick={() => setConfirming(true)}
                  disabled={busy}
                >
                  Cancel purchase
                </button>
              ) : (
                <div role="dialog" aria-label="Confirm cancellation">
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    Cancel this order? This voids your invoice and removes
                    access for everyone at your school.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                      onClick={handleConfirmedCancel}
                      disabled={busy}
                    >
                      {busy ? 'Cancelling…' : 'Confirm cancellation'}
                    </button>
                    <button
                      type="button"
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm"
                      onClick={() => setConfirming(false)}
                      disabled={busy}
                    >
                      Keep my order
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-600" data-testid="cancel-posture">
              {view.support_required || !view.is_purchaser
                ? SUPPORT_COPY
                : 'The 7-day cancellation window for this order has ended — contact support to discuss your options.'}
            </p>
          )}
        </>
      )}
    </div>
  );
}
